import { useState } from "react";
import { Image } from "lucide-react";
import { useTranslate } from "@/lib/i18n";
import { ApiError } from "@/lib/api/client";
import { endpoints } from "@/lib/api/endpoints";
import type { DispatchPaymentProofViewResponse } from "@/lib/api/dispatchTypes";
import { openDocumentInNewTab } from "@/modules/manage/lib/driverRoster";
import { useErrorMessage } from "@/shared/errors";
import { usePrivateApi } from "@/shared/usePrivateApi";
import { usePrivateToast } from "@/shared/usePrivateToast";

/**
 * «عرض الإثبات» — the driver's InstaPay screenshot for one delivered bag
 * (B6 · P25, plan D-42; T19 `POST …/assignments/:id/payment-proof/view`,
 * `DISPATCH_CASH_CONFIRM`).
 *
 * Every tap asks the server for a FRESH 60-second link and opens it in a new
 * tab exactly as the driver documents do (`openDocumentInNewTab`: the blank tab
 * opens inside the tap so no popup blocker stops it, `opener` is cut, then it
 * is pointed at the link). The link is never kept — not in state, not in the
 * query cache, not in the DOM: it goes from the answer straight to the tab.
 */
export function PaymentProofViewer({
  assignmentId,
  orderNumber,
}: {
  assignmentId: string;
  orderNumber: number;
}) {
  const t = useTranslate();
  const { apiFetch } = usePrivateApi();
  const toast = usePrivateToast();
  const errorMessage = useErrorMessage();
  const [busy, setBusy] = useState(false);

  const open = async () => {
    setBusy(true);
    try {
      const result = await openDocumentInNewTab(() =>
        apiFetch<DispatchPaymentProofViewResponse>(endpoints.restaurant.dispatchPaymentProofView(assignmentId), {
          method: "POST",
        }),
      );
      if (result === "blocked") toast.error(t((m) => m.dispatch.docPopupBlocked));
    } catch (error) {
      if (error instanceof ApiError && error.code === "DISPATCH_PAYMENT_PROOF_NOT_FOUND") {
        toast.error(t((m) => m.dispatch.settleProofGone));
      } else {
        toast.error(t((m) => m.dispatch.settleProofFailed, { error: errorMessage(error) }));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      data-ui="settle-proof"
      disabled={busy}
      aria-busy={busy || undefined}
      data-testid={`settle-proof-${orderNumber}`}
      onClick={() => void open()}
      className="inline-flex items-center"
    >
      <Image aria-hidden="true" size={13} strokeWidth={2} />
      <span>{t((m) => m.dispatch.settleViewProof)}</span>
    </button>
  );
}
